import type { Book, Chapter } from '../types';
import { useApp } from '../store/app';
import { rememberDuration } from '../lib/durations';
import { coverUrlOf, useCovers } from '../store/covers';

/**
 * The playback engine.
 *
 * One long-lived <audio> element lives here, outside React, so that navigating
 * between the library and the player never interrupts sound. React only ever
 * mirrors what this module reports into the store.
 */

export const SKIP_BACK_SEC = 15;
export const SKIP_FORWARD_SEC = 30;
export const SPEEDS = [0.75, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5] as const;

/** Progress is written at most this often; a timeupdate fires ~4x a second and
 *  re-rendering the library that fast for no visible gain is wasteful. */
const RECORD_INTERVAL_SEC = 2;
const FADE_OUT_MS = 6_000;

let audio: HTMLAudioElement | null = null;
let book: Book | null = null;
let chapters: Chapter[] = [];
let lastRecordedSec = -Infinity;
let sleepTicker: ReturnType<typeof setInterval> | null = null;
let fadeHandle: ReturnType<typeof setInterval> | null = null;

const store = () => useApp.getState();

function ensureAudio(): HTMLAudioElement {
  if (audio) return audio;

  const el = new Audio();
  el.preload = 'metadata';
  // No crossOrigin: every URL we play is same-origin — demo files from public/,
  // and Drive files through the service worker's /__drive/ path.
  setPreservesPitch(el, true);

  el.addEventListener('timeupdate', () => {
    const pos = el.currentTime;
    store().patchPlayback({ positionSec: pos });
    maybeRecord(pos);
    checkChapterEndSleep(pos);
  });

  el.addEventListener('loadedmetadata', () => {
    store().patchPlayback({ trackDurationSec: el.duration || 0 });
    cacheTrackDuration(el.duration);
    updatePositionState();
  });

  el.addEventListener('play', () => {
    store().patchPlayback({ playing: true, playbackError: null });
    syncMediaSessionPlaybackState();
  });
  el.addEventListener('pause', () => {
    store().patchPlayback({ playing: false });
    syncMediaSessionPlaybackState();
    void store().flushProgress();
  });
  el.addEventListener('waiting', () => store().patchPlayback({ buffering: true }));
  el.addEventListener('playing', () => store().patchPlayback({ buffering: false }));
  el.addEventListener('canplay', () => store().patchPlayback({ buffering: false }));
  el.addEventListener('ended', () => void handleTrackEnded());
  el.addEventListener('ratechange', () => store().patchPlayback({ speed: el.playbackRate }));

  el.addEventListener('error', () => {
    const code = el.error?.code;
    store().patchPlayback({
      buffering: false,
      playing: false,
      playbackError:
        code === MediaError.MEDIA_ERR_NETWORK ? 'Network error while streaming this file.'
        : code === MediaError.MEDIA_ERR_DECODE ? 'This file could not be decoded.'
        : code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ?
          'This audio format is not supported by your browser.'
        : 'Playback failed.',
    });
  });

  // iOS in particular is far more willing to keep a detached-from-layout but
  // in-document element playing in the background, so park it in the DOM.
  el.setAttribute('aria-hidden', 'true');
  el.style.display = 'none';
  document.body.appendChild(el);

  audio = el;
  registerMediaSession();
  return el;
}

function setPreservesPitch(el: HTMLAudioElement, value: boolean): void {
  // Keeps voices sounding natural at 1.5x+ rather than chipmunked.
  const anyEl = el as HTMLAudioElement & {
    preservesPitch?: boolean;
    mozPreservesPitch?: boolean;
    webkitPreservesPitch?: boolean;
  };
  anyEl.preservesPitch = value;
  anyEl.mozPreservesPitch = value;
  anyEl.webkitPreservesPitch = value;
}

/* ------------------------------------------------------------------ *
 * Opening a book
 * ------------------------------------------------------------------ */

export async function openBook(
  next: Book,
  opts: { autoplay?: boolean; trackIndex?: number; positionSec?: number } = {},
): Promise<void> {
  const state = store();
  const el = ensureAudio();

  const alreadyOpen = book?.id === next.id;
  book = next;

  const saved = state.progress.books[next.id];
  const trackIndex = opts.trackIndex ?? saved?.trackIndex ?? 0;
  const positionSec = opts.positionSec ?? saved?.positionSec ?? 0;

  if (!alreadyOpen) {
    // Chapters may mean reading tags from several files the first time. Start
    // playback without waiting: until they arrive the book shows a chapter per
    // file, which is what effectiveChapters falls back to.
    chapters = [];
    state.patchPlayback({ currentBookId: next.id, chapters });
    void (state.source?.chaptersFor(next) ?? Promise.resolve([]))
      .then((found) => {
        if (book?.id !== next.id) return; // another book was opened meanwhile
        chapters = found;
        store().patchPlayback({ chapters: found });
      })
      .catch(() => undefined);
  }

  const shouldAutoplay = opts.autoplay ?? false;
  if (alreadyOpen && opts.trackIndex === undefined && opts.positionSec === undefined) {
    // Re-opening the book that is already loaded: leave playback untouched.
    if (shouldAutoplay && el.paused) await play();
    return;
  }

  await loadTrack(trackIndex, positionSec, shouldAutoplay);
}

async function loadTrack(index: number, positionSec: number, autoplay: boolean): Promise<void> {
  const state = store();
  if (!book || !state.source) return;

  const clamped = Math.min(Math.max(0, index), book.tracks.length - 1);
  const track = book.tracks[clamped];
  if (!track) return;

  const el = ensureAudio();

  // Swapping `src` stops the element, and the browser does not reliably fire
  // `pause` when it does. Without this the transport button would keep showing
  // "pause" over silent audio after switching books.
  if (!autoplay) el.pause();

  lastRecordedSec = -Infinity;
  state.patchPlayback({
    trackIndex: clamped,
    positionSec,
    trackDurationSec: track.durationSec ?? 0,
    buffering: true,
    playing: autoplay ? state.playing : false,
    playbackError: null,
  });

  let url: string;
  try {
    // May be async on Drive when it has to buffer the file itself.
    url = await state.source.trackUrl(track);
  } catch (err) {
    store().patchPlayback({
      buffering: false,
      playing: false,
      playbackError: err instanceof Error ? err.message : String(err),
    });
    return;
  }

  if (el.src !== url) {
    el.src = url;
    el.load();
  }

  await seekWhenReady(el, positionSec);
  updateMediaSessionMetadata();

  if (autoplay) await play();
  else store().patchPlayback({ buffering: false });
}

/** Seeking before metadata arrives is silently ignored by most browsers, so
 *  wait for a seekable range to exist before applying the resume position. */
function seekWhenReady(el: HTMLAudioElement, positionSec: number): Promise<void> {
  if (positionSec <= 0) return Promise.resolve();

  if (el.readyState >= HTMLMediaElement.HAVE_METADATA) {
    el.currentTime = positionSec;
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const onReady = () => {
      el.removeEventListener('loadedmetadata', onReady);
      el.removeEventListener('error', onReady);
      try {
        el.currentTime = positionSec;
      } catch {
        /* out-of-range seek: start from the beginning rather than fail */
      }
      resolve();
    };
    el.addEventListener('loadedmetadata', onReady, { once: true });
    el.addEventListener('error', onReady, { once: true });
  });
}

/* ------------------------------------------------------------------ *
 * Transport
 * ------------------------------------------------------------------ */

export async function play(): Promise<void> {
  const el = ensureAudio();
  cancelFade(el);
  try {
    await el.play();
  } catch (err) {
    // Autoplay policies reject play() that was not user-initiated.
    store().patchPlayback({
      playing: false,
      playbackError:
        err instanceof DOMException && err.name === 'NotAllowedError' ?
          'Your browser blocked autoplay — press play to start.'
        : 'Playback could not start.',
    });
  }
}

export function pause(): void {
  ensureAudio().pause();
}

export async function togglePlay(): Promise<void> {
  const el = ensureAudio();
  if (el.paused) await play();
  else pause();
}

export function seekTo(seconds: number): void {
  const el = ensureAudio();
  const max = Number.isFinite(el.duration) ? el.duration : seconds;
  el.currentTime = Math.min(Math.max(0, seconds), max);
  store().patchPlayback({ positionSec: el.currentTime });
  forceRecord(el.currentTime);
  updatePositionState();
}

/** Skipping back past the start of a track rolls into the previous one, which
 *  is what you want when you nod off at a chapter boundary. */
export function skipBack(seconds = SKIP_BACK_SEC): void {
  const el = ensureAudio();
  const target = el.currentTime - seconds;
  if (target >= 0) {
    seekTo(target);
    return;
  }
  const { trackIndex } = store();
  if (trackIndex > 0 && book) {
    const prev = book.tracks[trackIndex - 1];
    const prevDuration = prev?.durationSec ?? 0;
    const wasPlaying = !el.paused;
    void loadTrack(trackIndex - 1, Math.max(0, prevDuration + target), wasPlaying);
  } else {
    seekTo(0);
  }
}

export function skipForward(seconds = SKIP_FORWARD_SEC): void {
  const el = ensureAudio();
  const duration = el.duration;
  const target = el.currentTime + seconds;
  if (!Number.isFinite(duration) || target < duration) {
    seekTo(target);
    return;
  }
  void nextTrack(target - duration);
}

export async function nextTrack(carrySec = 0): Promise<void> {
  if (!book) return;
  const { trackIndex } = store();
  if (trackIndex >= book.tracks.length - 1) {
    pause();
    seekTo(ensureAudio().duration || 0);
    return;
  }
  await loadTrack(trackIndex + 1, Math.max(0, carrySec), !ensureAudio().paused);
}

export async function previousTrack(): Promise<void> {
  const el = ensureAudio();
  // Standard media behaviour: the first press restarts the current track.
  if (el.currentTime > 3) {
    seekTo(0);
    return;
  }
  const { trackIndex } = store();
  if (trackIndex > 0) await loadTrack(trackIndex - 1, 0, !el.paused);
  else seekTo(0);
}

export function setSpeed(rate: number): void {
  const el = ensureAudio();
  setPreservesPitch(el, true);
  el.playbackRate = rate;
  localStorage.setItem('shelf.speed', String(rate));
  store().patchPlayback({ speed: rate });
  updatePositionState();
}

export function restoreSpeed(): void {
  const saved = Number(localStorage.getItem('shelf.speed'));
  if (Number.isFinite(saved) && saved > 0) setSpeed(saved);
}

/* ------------------------------------------------------------------ *
 * Chapters
 * ------------------------------------------------------------------ */

export async function jumpToChapter(chapter: Chapter): Promise<void> {
  const { trackIndex } = store();
  if (chapter.trackIndex === trackIndex) {
    seekTo(chapter.startSec);
    return;
  }
  await loadTrack(chapter.trackIndex, chapter.startSec, !ensureAudio().paused);
}

/** Index of the chapter containing the current position, or -1. */
export function activeChapterIndex(
  list: Chapter[],
  trackIndex: number,
  positionSec: number,
): number {
  let found = -1;
  for (let i = 0; i < list.length; i++) {
    const c = list[i]!;
    if (c.trackIndex < trackIndex) found = i;
    else if (c.trackIndex === trackIndex && c.startSec <= positionSec + 0.25) found = i;
    else break;
  }
  return found;
}

/** End of the chapter currently playing, in track-local seconds. */
function currentChapterEnd(positionSec: number): number | null {
  const { trackIndex, trackDurationSec } = store();
  const idx = activeChapterIndex(chapters, trackIndex, positionSec);
  if (idx === -1) return trackDurationSec || null;
  const next = chapters[idx + 1];
  if (next && next.trackIndex === trackIndex) return next.startSec;
  return trackDurationSec || null;
}

/* ------------------------------------------------------------------ *
 * Sleep timer
 * ------------------------------------------------------------------ */

export function setSleepTimer(minutes: number | null): void {
  clearSleepTicker();
  cancelFade(ensureAudio());

  if (minutes === null) {
    store().patchPlayback({ sleepEndsAt: null, sleepAtChapterEnd: false });
    return;
  }

  const endsAt = Date.now() + minutes * 60_000;
  store().patchPlayback({ sleepEndsAt: endsAt, sleepAtChapterEnd: false });

  sleepTicker = setInterval(() => {
    const { sleepEndsAt } = store();
    if (sleepEndsAt === null) {
      clearSleepTicker();
      return;
    }
    if (Date.now() >= sleepEndsAt) {
      clearSleepTicker();
      store().patchPlayback({ sleepEndsAt: null });
      fadeOutAndPause();
    }
  }, 1_000);
}

export function setSleepAtChapterEnd(enabled: boolean): void {
  clearSleepTicker();
  store().patchPlayback({ sleepAtChapterEnd: enabled, sleepEndsAt: null });
}

function checkChapterEndSleep(positionSec: number): void {
  const { sleepAtChapterEnd } = store();
  if (!sleepAtChapterEnd) return;
  const end = currentChapterEnd(positionSec);
  if (end !== null && positionSec >= end - 0.4) {
    store().patchPlayback({ sleepAtChapterEnd: false });
    fadeOutAndPause();
  }
}

function clearSleepTicker(): void {
  if (sleepTicker !== null) {
    clearInterval(sleepTicker);
    sleepTicker = null;
  }
}

/** Ramps the volume down before pausing — waking to an abrupt cut is nasty. */
function fadeOutAndPause(): void {
  const el = ensureAudio();
  if (el.paused) return;

  const startVolume = el.volume;
  const steps = 30;
  let step = 0;

  cancelFade(el);
  fadeHandle = setInterval(() => {
    step += 1;
    el.volume = Math.max(0, startVolume * (1 - step / steps));
    if (step >= steps) {
      cancelFade(el);
      el.pause();
      el.volume = startVolume;
    }
  }, FADE_OUT_MS / steps);
}

function cancelFade(el: HTMLAudioElement): void {
  if (fadeHandle !== null) {
    clearInterval(fadeHandle);
    fadeHandle = null;
    el.volume = 1;
  }
}

/* ------------------------------------------------------------------ *
 * Progress recording
 * ------------------------------------------------------------------ */

function maybeRecord(positionSec: number): void {
  // Only ever record while sound is actually coming out.
  //
  // A freshly loaded element fires `timeupdate` at 0 before the resume seek has
  // been applied. Recording that would overwrite the saved position with zero
  // and lose your place — the exact failure this whole feature exists to
  // prevent. Explicit user actions (scrub, skip, chapter jump) record through
  // forceRecord instead and are unaffected.
  if (!audio || audio.paused) return;
  if (Math.abs(positionSec - lastRecordedSec) < RECORD_INTERVAL_SEC) return;
  forceRecord(positionSec);
}

function forceRecord(positionSec: number): void {
  if (!book) return;
  lastRecordedSec = positionSec;
  store().recordProgress(book.id, store().trackIndex, positionSec);
}

/** Track durations are not known until the file loads; cache them on the book
 *  so total-length and "time left" figures firm up as you listen, and remember
 *  them so the library knows them next time without reopening the book. */
function cacheTrackDuration(duration: number): void {
  if (!book || !Number.isFinite(duration) || duration <= 0) return;
  const { trackIndex, books } = store();
  const track = book.tracks[trackIndex];
  if (!track) return;
  void rememberDuration(track, duration);
  if (track.durationSec === duration) return;

  track.durationSec = duration;
  const bookId = book.id;
  const updated = books.map((b) =>
    b.id !== bookId ? b : (
      {
        ...b,
        tracks: b.tracks.map((t, i) =>
          i === trackIndex ? { ...t, durationSec: duration } : t,
        ),
      }
    ),
  );
  store().patchPlayback({ books: updated });
}

async function handleTrackEnded(): Promise<void> {
  if (!book) return;
  const { trackIndex, sleepAtChapterEnd } = store();

  if (sleepAtChapterEnd) {
    store().patchPlayback({ sleepAtChapterEnd: false, playing: false });
    return;
  }
  if (trackIndex >= book.tracks.length - 1) {
    forceRecord(ensureAudio().duration || 0);
    await store().flushProgress();
    store().patchPlayback({ playing: false });
    return;
  }
  await loadTrack(trackIndex + 1, 0, true);
}

/* ------------------------------------------------------------------ *
 * Media Session — lock screen / headset controls
 * ------------------------------------------------------------------ */

function registerMediaSession(): void {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;

  const safe = (action: MediaSessionAction, handler: MediaSessionActionHandler) => {
    try {
      ms.setActionHandler(action, handler);
    } catch {
      /* Not every browser implements every action. */
    }
  };

  safe('play', () => void play());
  safe('pause', () => pause());
  safe('seekbackward', (d) => skipBack(d.seekOffset ?? SKIP_BACK_SEC));
  safe('seekforward', (d) => skipForward(d.seekOffset ?? SKIP_FORWARD_SEC));
  safe('previoustrack', () => void previousTrack());
  safe('nexttrack', () => void nextTrack());
  safe('seekto', (d) => {
    if (typeof d.seekTime === 'number') seekTo(d.seekTime);
  });
  safe('stop', () => pause());
}

function updateMediaSessionMetadata(): void {
  if (!('mediaSession' in navigator) || !book) return;
  const { trackIndex } = store();
  const track = book.tracks[trackIndex];

  navigator.mediaSession.metadata = new MediaMetadata({
    title: track?.name ?? book.title,
    artist: book.author ?? 'Audiobook',
    album: book.title,
    artwork: coverUrlOf(book) ? [{ src: coverUrlOf(book) ?? '', sizes: '400x400' }] : [],
  });
}

function syncMediaSessionPlaybackState(): void {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.playbackState = audio && !audio.paused ? 'playing' : 'paused';
  updatePositionState();
}

function updatePositionState(): void {
  if (!('mediaSession' in navigator) || !audio) return;
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
  try {
    navigator.mediaSession.setPositionState({
      duration: audio.duration,
      playbackRate: audio.playbackRate,
      position: Math.min(audio.currentTime, audio.duration),
    });
  } catch {
    /* Safari throws if position briefly exceeds duration. */
  }
}

/* ------------------------------------------------------------------ */

// Embedded cover art can arrive after the book has opened; refresh the lock
// screen when it does.
useCovers.subscribe(() => updateMediaSessionMetadata());

export function getAudioElement(): HTMLAudioElement {
  return ensureAudio();
}

/** Persist immediately when the tab is hidden or closed — this is the moment
 *  that makes "stop on the laptop, resume on the phone" actually work. */
export function installLifecycleHooks(): () => void {
  const onHide = () => {
    if (document.visibilityState === 'hidden') void store().flushProgress();
  };
  const onPageHide = () => void store().flushProgress();

  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
  };
}
